import React from 'react';
import {
  FormControlLabel,
  FormGroup,
  Checkbox,
  Typography,
  Box,
  checkboxClasses
} from '@mui/material';
import { flexrow } from '../../global';
import AudienceIcon2 from '../../images/audienceIcon2.svg';

export interface AudienceDistributionItem {
  type: string;
  count: string;
}

export interface DistributionListSelectorProps {
  distributionList: string[];
  onDistributionListChange: (list: string[]) => void;
  disabled?: boolean;
  overHundredParticipants?: boolean;
  className?: string;
  sx?: any;
}

// WorkforceGPS Distribution List Data
const audienceDistributionData: AudienceDistributionItem[] = [
  {
    type: 'All users of WorkforceGPS',
    count: '131k'
  },
  {
    type: 'American Job Centers',
    count: '27k'
  },
  {
    type: 'Businesses/Employers',
    count: '12k'
  },
  {
    type: 'Direct providers of employment and training services',
    count: '24k'
  },
  {
    type: 'Economic Development',
    count: '13k'
  },
  {
    type: 'Education Institutions',
    count: '24k'
  },
  {
    type: 'Government - Federal',
    count: '13k'
  },
  {
    type: 'Government - Local',
    count: '15k'
  },
  {
    type: 'Government - State',
    count: '35k'
  },
  {
    type: 'Government - Tribal',
    count: '4k'
  },
  {
    type: 'Labor Organizations',
    count: '9k'
  },
  {
    type: 'Not-For-Profits',
    count: '36k'
  },
  {
    type: 'Workforce Investment Board - Staff or Members',
    count: '14k'
  },
  {
    type: 'Region 1',
    count: '20k'
  },
  {
    type: 'Region 2',
    count: '19k'
  },
  {
    type: 'Region 3',
    count: '25k'
  },
  {
    type: 'Region 4',
    count: '21k'
  },
  {
    type: 'Region 5',
    count: '28k'
  },
  {
    type: 'Region 6',
    count: '27k'
  },
  {
    type: 'Other',
    count: '18k'
  }
];

export const DistributionListSelector: React.FC<DistributionListSelectorProps> = ({
  distributionList,
  onDistributionListChange,
  disabled = false,
  overHundredParticipants = true,
  className,
  sx
}) => {
  const handleChangeDistributionList = (event: any) => {
    if (event.target.checked) {
      if (event.target.value === '0') {
        // Select "All users" - this should clear other selections and select only this one
        onDistributionListChange([audienceDistributionData[event.target.value].type]);
      } else {
        // Add to existing selections
        onDistributionListChange([
          ...distributionList,
          audienceDistributionData[event.target.value].type
        ]);
      }
    } else {
      // Remove from selections
      const filteredArray = distributionList.filter(
        e => e !== audienceDistributionData[event.target.value].type
      );
      onDistributionListChange(filteredArray);
    }
  };

  return (
    <Box sx={{ 
      ...sx, 
      paddingRight: '8px',
      paddingLeft: '8px',
      width: '100%'
    }} className={className}>
      <FormGroup>
        {audienceDistributionData.map((data, index) => {
          let listChecked = false;
          if (distributionList.includes(data.type)) {
            listChecked = true;
          }
          
          // Hide "All users" if not over 100 participants
          if (!overHundredParticipants && index === 0) {
            return <React.Fragment key={index}></React.Fragment>;
          }
          
          return (
            <FormControlLabel
              key={index}
              sx={{
                mb: 1,
                mt: 0,
                width: '100%',
                '.MuiFormControlLabel-label': {
                  width: '100%'
                },
                '.MuiFormControlLabel-root': {
                  marginRight: 0,
                  marginLeft: 0
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'flex-start'
              }}
              disabled={
                disabled ||
                (index > 0 && distributionList.includes(audienceDistributionData[0].type))
              }
              value={index}
              checked={
                (index > 0 && distributionList.includes(audienceDistributionData[0].type)) ||
                listChecked
              }
              onClick={handleChangeDistributionList}
              control={
                <Checkbox
                  size='small'
                  style={{
                    paddingTop: '3px',
                    padding: '3px'
                  }}
                  sx={{
                    [`&, &.${checkboxClasses.checked}`]: {
                      color: '#565656'
                    },
                    [`&.${checkboxClasses.disabled}`]: {
                      color: 'rgba(86, 86, 86, 0.5)'
                    }
                  }}
                />
              }
              label={
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    padding: '3px 0 0 9px'
                  }}
                >
                  <Typography
                    sx={{
                      float: 'left',
                      fontSize: '14px',
                      lineHeight: '27.2px'
                    }}
                  >
                    {data.type}
                    {'  '}
                  </Typography>
                  <Box
                    style={flexrow}
                    sx={{
                      padding: '2px 0 0 8px',
                      width: '60px',
                      justifyContent: 'flex-start'
                    }}
                  >
                    <Box
                      component='img'
                      src={AudienceIcon2}
                      sx={{
                        padding: '0 8px 0 0',
                        opacity:
                          index > 0 && distributionList.includes(audienceDistributionData[0].type)
                            ? 0.4
                            : 1
                      }}
                    />
                    <Typography>{data.count}</Typography>
                  </Box>
                </Box>
              }
            />
          );
        })}
      </FormGroup>
    </Box>
  );
};

// Export the data for any components that might need it directly
export { audienceDistributionData };
